import { motion } from 'framer-motion';
import { useState } from 'react';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  viewport: { once: true, margin: '-100px' },
};

const CommunityPage = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const affiliations = [
    { name: 'BNI', description: 'Business Network International', logo: process.env.PUBLIC_URL + '/images/logo_bni.webp' },
    { name: 'Rotary Club', description: 'Service Above Self', logo: process.env.PUBLIC_URL + '/images/logo_rotary.webp' },
    { name: 'Sambhav Foundation', description: 'Community Development', logo: process.env.PUBLIC_URL + '/images/logo_sambhav.webp' },
    { name: 'SGPC', description: 'Shiromani Gurdwara Parbandhak Committee', logo: process.env.PUBLIC_URL + '/images/logo_sgpc.webp' },
  ];

  return (
    <div data-testid="community-page" className="pt-32 pb-24 bg-[#F2F0E9]">
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <motion.div {...fadeInUp} className="text-center mb-24">
          <h1 className="hero-title text-[#1A1A1A] mb-6">
            Building <span className="italic">Community</span>
          </h1>
          <p className="text-lg md:text-xl text-[#595959] max-w-3xl mx-auto leading-relaxed">
            Trust, integrity, and social responsibility are the pillars of our business. We're proud to be part of organizations that make a difference.
          </p>
        </motion.div>

        {/* Values Section */}
        <motion.div {...fadeInUp} data-testid="values-section" className="mb-32">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            <div>
              <h3 className="serif text-2xl text-[#1A1A1A] mb-4 accent-cyan">Trust</h3>
              <p className="text-base text-[#595959] leading-relaxed">
                Two decades of transparent business relationships built on honesty and reliability.
              </p>
            </div>
            <div>
              <h3 className="serif text-2xl text-[#1A1A1A] mb-4 accent-magenta">Excellence</h3>
              <p className="text-base text-[#595959] leading-relaxed">
                Uncompromising commitment to quality in every product and service we deliver.
              </p>
            </div>
            <div>
              <h3 className="serif text-2xl text-[#1A1A1A] mb-4 accent-yellow">Community</h3>
              <p className="text-base text-[#595959] leading-relaxed">
                Active participation in social initiatives that strengthen our local communities.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Affiliations */}
        <motion.div {...fadeInUp}>
          <h2 className="section-title text-center text-[#1A1A1A] mb-16">
            Our Affiliations
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {affiliations.map((affiliation, index) => (
              <motion.div
                key={index}
                data-testid={`affiliation-${index}`}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                className="flex flex-col items-center justify-center p-8 bg-white border border-[#D6D3C9] transition-all duration-500 hover:shadow-lg"
                style={{
                  filter: hoveredIndex === null || hoveredIndex === index ? 'grayscale(0)' : 'grayscale(1)',
                }}
              >
                {affiliation.logo ? (
                  <div className="w-32 h-32 flex items-center justify-center mb-4">
                    <img
                      loading="lazy"
                      decoding="async"
                      src={affiliation.logo}
                      alt={affiliation.name}
                      width="128"
                      height="128"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-32 h-32 flex items-center justify-center border-2 border-[#D6D3C9] rounded-full mb-4">
                    <span className="cinzel text-2xl font-semibold text-[#1A1A1A]">
                      {affiliation.name}
                    </span>
                  </div>
                )}
                <p className="text-xs text-center text-[#595959] uppercase tracking-widest">
                  {affiliation.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Impact Statement */}
        <motion.div
          {...fadeInUp}
          data-testid="impact-statement"
          className="mt-32 bg-[#1A1A1A] text-[#F2F0E9] py-20 px-6 md:px-12 text-center"
        >
          <h2 className="serif text-3xl md:text-4xl mb-6">
            Beyond Business
          </h2>
          <p className="text-lg max-w-3xl mx-auto leading-relaxed">
            Our commitment to community extends beyond commercial relationships. Through active participation in social and charitable organizations, we strive to create positive impact and contribute to the greater good.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default CommunityPage;
